import type { WrittenPage } from '../sections'
import { LINKS } from './links'

/**
 * The Agentic AI e-book's own page.
 *
 * Built to the design of the digitopia.co page it replaces
 * (`/impact-labs/ebooks/agentic-ai/`), sent as four screenshots on 2026-08-28
 * with *"referans alarak ilgili sayfayı tastamam şekilde, aynısını, tıpkısını
 * tasarla"*. So the structure here is that page's, section for section: the
 * two-column hero with the cover artwork beside it, the grey "Why Download?"
 * card with the cover pair in it, the "What You'll Learn" card with the six
 * bullets and the stacked copies, and the centred download ask at the end.
 * The copy is that page's copy, verbatim.
 *
 * Second e-book page after Transform Better, and the same reasoning applies to
 * both: it is a written page rather than the `content/ebooks.ts` template
 * because the template is a cover, a series paragraph and a button — the shape
 * you have for sixteen e-books nobody has designed a page for. The catalogue's
 * `agentic-ai` entry now carries `href` to here, which is also what stops
 * `prerender.tsx` generating the template over the top of this file.
 *
 * Three things are deliberately not the reference:
 *
 *  * **The eyebrow is the site's own**, centred over the copy column at half the
 *    headline's size, rather than digitopia.co's small left-pinned label. Every
 *    other hero here is that shape (About, Solutions, Tools, Partner Program) and
 *    one page disagreeing with four is worse than one page disagreeing with a
 *    screenshot.
 *  * **The download is a direct link to the PDF, not a gated form.** The
 *    reference puts a nine-field HubSpot form between the reader and the file;
 *    this site's forms submit nowhere yet (see the `check` on the home page's),
 *    so reproducing it would mean the page's only call to action silently drops
 *    every lead. All four buttons therefore open the same PDF, which is also
 *    what the Transform Better page does. The `check` below says so.
 *  * **The testimonial at the foot is ours, not the reference's.** Asked for in
 *    the same message — *"en aşağıda önceden konuştuğumuz gibi testimonial
 *    template ini kullanalım"* — so it is the Hall of Fame card, and the card is
 *    Intel AI's: of the twenty in `content/testimonials.ts` it is the one about
 *    AI readiness rather than digital maturity, and its mark and portrait are
 *    both in the 900px batch. Its words are the Hall of Fame's; that file is
 *    where a change to them belongs.
 */
export const ebookAgenticAi: WrittenPage = {
  navLabel: 'Agentic AI',
  title: 'Agentic AI: A Practical Guide',
  description:
    'Redefining how businesses think, decide, and act with autonomous AI agents. Download the Agentic AI practical guide from Digitopia Impact Labs.',
  sections: [
    {
      kind: 'hero',
      eyebrow: 'A Practical Guide',
      headline: 'Agentic AI',
      sub: 'Redefining how businesses think, decide, and act with autonomous AI agents.',
      body: [
        'Explore how autonomous AI agents are reshaping business by planning, learning, and '
          + 'acting in real time. See how top organizations are gaining speed, efficiency, and '
          + 'strategic edge with agentic AI.',
      ],
      ctas: [{ label: 'Download', href: LINKS.agenticAiEbookPdf }],
      photo: {
        src: '/photo/agentic-ai-ebook-artwork.webp',
        alt: 'The e-book’s cover artwork: a sphere of blue and violet points of light against black.',
      },
      // The artwork is square and the site's hero box is 3:2, which would take a
      // third of the sphere off the top and bottom.
      photoFrame: 'square',
      /*
        Clause 1 — THE DOWNLOAD IS NOT GATED — resolved 2026-08-30. It said no
        form on this site submits anywhere and the Send button is disabled on
        purpose. Both stopped being true today: the form posts to
        `website-form-lead`, the submission opens a task on its page's own
        counterpart, and every Download here carries `data-gated-download`, so
        the reader is asked for an email and the PDF is sent to it. No HubSpot
        form id is needed for any of it. What is left is clause 2.
      */
      check:
        'THE COVER ARTWORK IS 768×768. It is the largest copy of it anybody '
        + 'has sent; every other hero on the site is 2560px, and this one is painted '
        + 'at roughly its own width on a laptop with nothing left over for a retina '
        + 'screen. It is generated art rather than a photograph, so a bigger export '
        + 'should exist wherever it was made:\n'
        + '     node apps/website/scripts/attach.mjs <file> photo-src/agentic-ai-ebook-artwork.webp\n'
        + '     pnpm --filter @digitopia/website images\n'
        + '   and nothing in this file changes. The same is true of the two mockups '
        + 'below it (900px wide, both), which are drawn smaller and are therefore '
        + 'less pressing.',
    },
    {
      kind: 'cta',
      tone: 'card',
      // Artwork left, offer right — the reference's order, and it alternates
      // with the card under it.
      flip: true,
      heading: 'Why Download?',
      sub:
        'Agentic AI unlocks a new era of “digital coworkers” that reduce complexity, eliminate '
        + 'repetitive work, and accelerate decision-making. Whether you’re piloting new '
        + 'technologies or planning enterprise-wide transformation, this ebook equips you with the '
        + 'insights to take your strategy beyond reactive AI and into a future of intelligent '
        + 'autonomy.',
      ctas: [{ label: 'Download the Ebook', href: LINKS.agenticAiEbookPdf }],
      photo: {
        src: '/photo/agentic-ai-ebook-covers.webp',
        alt: 'Two copies of the Agentic AI guide, one behind the other, on a pale gradient.',
      },
    },
    {
      kind: 'cta',
      tone: 'card',
      heading: 'What You\'ll Learn',
      sub:
        'Explore the real-world potential of agentic AI, well beyond chatbots and simple '
        + 'automation. This guide includes:',
      points: [
        'Core concepts and characteristics of agentic AI',
        'Strategic benefits and business value',
        'Use cases from operations, customer service, R&D, and IT',
        'Risks, governance, and ethical frameworks',
        'Practical steps to implement and scale agentic systems',
        'Future trends, including AI agents as digital coworkers',
      ],
      ctas: [{ label: 'Download the Ebook', href: LINKS.agenticAiEbookPdf }],
      photo: {
        src: '/photo/agentic-ai-ebook-stack.webp',
        alt: 'A stack of printed copies of the Agentic AI guide seen from above.',
      },
    },
    {
      kind: 'prose',
      align: 'center',
      heading: 'Download the Ebook',
      sub: 'Get the ebook and start building your agentic AI strategy today.',
      cta: { label: 'Download the Ebook', href: LINKS.agenticAiEbookPdf },
    },
    {
      kind: 'quotes',
      variant: 'fame',
      heading: 'What our customers say.',
      items: [
        {
          // The Hall of Fame's Intel AI card, word for word — `content/testimonials.ts`
          // is where these words are edited, and the Turkish page carries its own
          // saying of them.
          quote:
            'Intel’s AI Readiness Assessment, built on Digitopia’s intuitive, self-service '
            + 'platform, empowers customers and prospects to independently evaluate their AI '
            + 'maturity. The resulting clear, actionable report helps identify and bridge '
            + 'organizational gaps, while also fostering trust in Intel as a strategic AI '
            + 'advisor. The assessment starts the conversation, equips customers with actionable '
            + 'insights, and creates valuable opportunities for ongoing engagement.',
          source: 'Victoria Kouyoumjian, Marketing Director, Intel AI',
          company: 'Intel AI',
          logo: { name: 'Intel AI', src: '/logo/intelaicard.png' },
          portrait: {
            src: '/photo/portrait-victoria-kouyoumjian.webp',
            alt: 'Victoria Kouyoumjian',
          },
        },
      ],
    },
  ],
}
